import { useMutation, useQuery } from '@apollo/client'
import { useConfirm, useNodes, usePermissions } from '@/utils/hooks'
import { Header, OldSection, SectionHeader } from '@/modules/companies'
import { Table } from '@/components_deprecated/Table'
import {
  Icon,
  Loader,
  usePrompt,
  useToast,
} from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { SalaryGroupForm } from './types'
import type { SalaryGroupNode } from './types.graphql'
import {
  CREATE_SALARY_GROUP,
  DELETE_SALARY_GROUP,
  PATCH_SALARY_GROUP,
} from './mutations'
import { PERMISSIONS } from '@/utils/permissions'
import { ALL_SALARY_GROUPS } from './queries'
import { EditSalaryGroupPrompt } from './EditSalaryGroupPrompt'
import { getNodes } from '@/utils/graphql'
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination'
import type { AllUsersQuery, AllUsersQueryVariables } from '@/modules/users'
import { USERS_QUERY } from '@/modules/users/queries'
import { H2 } from '@/components'
import { Button, FAIcon, SVGIcon } from '@/components_deprecated'
import { isMobileOnly } from 'react-device-detect'

interface SalaryGroupsProps {}

export const SalaryGroups: React.JSX.CFC<SalaryGroupsProps> = () => {
  const translations = useTranslate({
    salaryGroups: 'salary.salary-groups',

    name: 'common.name',
    edit: 'common.edit',
    delete: 'common.delete',
    add: 'common.add',

    deleteSalaryGroup: 'salary.delete-salary-group',
    deleteSalaryGroupConfirm: 'salary.delete-salary-group-confirm',

    toast: {
      successCreate: 'salary.salary-group-create-success',
      successUpdate: 'salary.salary-group-update-success',
      successDelete: 'salary.salary-group-delete-success',
    },
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()

  const canAdd = hasPermissions(PERMISSIONS.salary.add.timeaddon, true)

  const canEdit = hasPermissions(PERMISSIONS.salary.change.timeaddon, true)
  const canDelete = hasPermissions(PERMISSIONS.salary.delete.timeaddon, true)

  const { data, loading } = useQuery(ALL_SALARY_GROUPS)

  const { data: userData } = useQuery<AllUsersQuery, AllUsersQueryVariables>(
    USERS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        includeInactive: false,
        onlyIncludeUserDocumentCategories: [],
      },
    }
  )

  const companyUsers =
    getNodes(userData?.allUsers).map(user => ({
      id: user.id,
      fullName: user.fullName,
    })) ?? []

  const refetchQueries = [
    {
      query: ALL_SALARY_GROUPS,
    },
    'DriverActivityPeriodReport',
    'DriverActivityPeriodReportShallow',
    'AllDynamicColumnsQuery',
    'DriverActivityDayReport',
  ]

  const [createSalaryGroup] = useMutation(CREATE_SALARY_GROUP, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successCreate)
    },
  })

  const [patchSalaryGroup] = useMutation(PATCH_SALARY_GROUP, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successUpdate)
    },
  })

  const [deleteSalaryGroup] = useMutation(DELETE_SALARY_GROUP, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successDelete)
    },
  })

  const salaryGroups = useNodes(data?.allSalaryGroups)

  const handleAdd = async () => {
    const { data } = await addPrompt<SalaryGroupForm | null>(resolve => (
      <EditSalaryGroupPrompt companyUsers={companyUsers} onSubmit={resolve} />
    ))

    if (!data) return

    void createSalaryGroup({
      variables: {
        input: {
          name: data.name,
          users: data.users,
          responsibleUsers: data.responsibleUsers,
        },
      },
    })
  }

  const handleEdit = async (salaryGroup: SalaryGroupNode) => {
    const initialData = {
      name: salaryGroup.name,
      users: getNodes(salaryGroup.users).map(user => user.id),
      responsibleUsers: getNodes(salaryGroup.responsibleUsers).map(
        user => user.id
      ),
    }
    const { data } = await addPrompt<SalaryGroupForm | null>(resolve => (
      <EditSalaryGroupPrompt
        initialData={initialData}
        companyUsers={companyUsers}
        onSubmit={resolve}
      />
    ))

    if (!data) return

    void patchSalaryGroup({
      variables: {
        id: salaryGroup.id,
        input: {
          name: data.name,
          users: data.users,
          responsibleUsers: data.responsibleUsers,
        },
      },
    })
  }

  const handleDelete = async (salaryGroup: SalaryGroupNode) => {
    const { data } = await confirm(
      translations.deleteSalaryGroupConfirm,
      translations.deleteSalaryGroup
    )

    if (!data) return

    void deleteSalaryGroup({
      variables: {
        id: salaryGroup.id,
      },
    })
  }

  const numUtilColumns = (canEdit ? 1 : 0) + (canDelete ? 1 : 0)

  return (
    <OldSection>
      <Header>
        <SectionHeader>
          <SVGIcon icon={'users'} fill="black" width={24} />
          <H2>{translations.salaryGroups}</H2>
          {loading && <Loader.Spinner />}
        </SectionHeader>
        <div>
          {canAdd && isMobileOnly ? (
            <Button height="32px" padding="0 1rem" onClick={handleAdd}>
              <FAIcon icon="plus" $size="0.8rem" />
            </Button>
          ) : canAdd ? (
            <Button
              height="36px"
              padding="0 2rem"
              iconLeftProps={{
                icon: 'plus',
              }}
              onClick={handleAdd}
            >
              {translations.add}
            </Button>
          ) : null}
        </div>
      </Header>
      {!loading && (
        <Table
          noShadow
          noBorders
          noHeaderMargin
          noRowMargin
          rowHoverColor="secondary"
        >
          <thead>
            <tr>
              <th>{translations.name}</th>
              {(canEdit || canDelete) && <th colSpan={numUtilColumns}></th>}
            </tr>
          </thead>
          <tbody>
            {salaryGroups.map(salaryGroup => (
              <tr key={salaryGroup.id}>
                <td>{salaryGroup.name}</td>
                {canEdit && (
                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEdit(salaryGroup as SalaryGroupNode)}
                    />
                  </td>
                )}
                {canDelete && (
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() =>
                        handleDelete(salaryGroup as SalaryGroupNode)
                      }
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </OldSection>
  )
}
